import { api } from "../api/client";

/**
 * The panel's half of chat (M0.10, spec §10.2, §13.2).
 *
 * Types hand-written against `packages/functions/src/chat-token.ts` and `show-chat.ts`, as
 * everywhere else in this panel: there is no schema between the two, so this file is the contract,
 * and what keeps it honest is that both halves live in one repository and change together.
 *
 * ## The panel joins the room through the *public* route
 *
 * `POST /api/public/shows/{publicId}/chat-token` — the same route a viewer uses, on the panel's
 * hostname. A host is a participant in their own room: they read the feed and they talk in it, and
 * the token they get is the same shape as anybody's plus a `badge` attribute (`R-MOD-11`) — which
 * a token minted for the same account on the *customer* application deliberately does not carry.
 *
 * **What it does not carry is any power.** Moderation is the routes below, which are gated,
 * audited, and go through this platform rather than over the WebSocket — `R-MOD-2`, and
 * `packages/functions/src/show-chat.ts` explains why that is not merely a preference.
 */

export interface ChatTokenResponse {
  chat: {
    token: string;
    sessionExpiresAt: string | null;
    region: string;
    maxMessageLength: number;
  };
  canSend: boolean;
  reason?: "anonymous" | "no-name" | "muted";
  userId: string;
  displayName: string | null;
  /** Who this account is. Unused by the client; the badge below is the thing that is drawn. */
  role: string | null;
  /** `host`, `moderator`, or null — what this token's comments will carry (`R-MOD-11`). */
  badge: string | null;
}

export const chatToken = (publicId: string) =>
  api.post<ChatTokenResponse>(
    `/api/public/shows/${encodeURIComponent(publicId)}/chat-token`,
  );

export interface Muted {
  accountId: string;
  displayName: string | null;
  mutedAt: string;
  mutedBy: string;
}

export interface ChatOverview {
  mutes: Muted[];
  /** Whether a room exists and has not been deleted — "no comments yet" against "broken". */
  room: { open: boolean };
}

export const moderation = {
  read: (showId: string) =>
    api.get<ChatOverview>(`/api/shows/${encodeURIComponent(showId)}/chat`),

  /**
   * Spec §10.2 — *deletes it for everyone, in real time.* `senderId` is sent so the audit row
   * records who was moderated as well as what (`R-MOD-2`); the message text is deliberately not
   * recorded anywhere, because a comment removed from the room and kept in a log has not been
   * removed.
   */
  remove: (showId: string, messageId: string, senderId: string) =>
    api.post<{ deleted: boolean }>(
      `/api/shows/${encodeURIComponent(showId)}/chat/delete`,
      { messageId, senderId },
    ),

  /**
   * `R-MOD-1` — for this show, and it cannot be for longer: the sanction is a row keyed on
   * `(show, account)` against a room that is deleted when the show ends.
   *
   * `disconnected` comes back because the panel should not claim an instant it did not get — see
   * `packages/core/src/chat/mutes.ts` for why a mute involves a disconnect at all.
   */
  mute: (showId: string, accountId: string) =>
    api.post<{ muted: boolean; disconnected: boolean }>(
      `/api/shows/${encodeURIComponent(showId)}/chat/mute`,
      { accountId },
    ),

  unmute: (showId: string, accountId: string) =>
    api.post<{ muted: boolean; lifted: boolean; disconnected: boolean }>(
      `/api/shows/${encodeURIComponent(showId)}/chat/unmute`,
      { accountId },
    ),
};

/**
 * Who a commenter is (`R-MOD-1b`), hand-written against `packages/functions/src/customer.ts`.
 *
 * The addresses are Shopify's, read at the moment of asking and never kept — `shopify.status`
 * says why there are none when there are none.
 */
export interface CustomerAddress {
  id: string;
  /** Shopify's own line-by-line rendering, in the order that country writes addresses. */
  formatted: string[];
  isDefault: boolean;
}

export interface CustomerDetails {
  account: {
    id: string;
    displayName: string | null;
    email: string;
    joinedAt: string;
    staff: boolean;
  };
  shopify: {
    status: "ok" | "not-installed" | "not-mirrored" | "unreachable";
    customerUrl: string | null;
    addresses: CustomerAddress[];
  };
  /** The host's mute in every show (`R-MOD-1a`), or null. */
  chatMute: { mutedAt: string; mutedBy: string; mutedByName: string | null } | null;
}

export const customers = {
  /** `showId` is only for the audit row: which show's feed the details were opened from. */
  read: (accountId: string, showId?: string) =>
    api.get<CustomerDetails>(
      `/api/customers/${encodeURIComponent(accountId)}` +
        (showId ? `?showId=${encodeURIComponent(showId)}` : ""),
    ),

  /** `R-MOD-1a` — host only; the server answers a moderator 403. */
  muteEverywhere: (accountId: string) =>
    api.post<{ muted: true; rooms: number; disconnected: boolean }>(
      `/api/customers/${encodeURIComponent(accountId)}/mute`,
    ),

  unmuteEverywhere: (accountId: string) =>
    api.post<{ muted: false; lifted: boolean; disconnected: boolean }>(
      `/api/customers/${encodeURIComponent(accountId)}/unmute`,
    ),
};
